/**
 * Contract-termination hooks (TanStack Query).
 *
 * Drives the channel-agnostic termination workflow:
 *  - useInitiateTermination — POST /contract-terminations (creates the item)
 *  - useTerminationPlan     — GET  /contract-terminations/:id/plan (dry-run)
 *  - useProcessTermination  — POST /contract-terminations/:id/process (saga)
 *  - useRevokePlan          — GET  /contract-groups/:id/revoke-plan (#1678)
 *  - useRevokeTermination   — POST /contract-groups/:id/revoke (#1678)
 *
 * Cancellation of colliding invoices runs SERVER-SIDE inside `process`; the UI
 * never calls #722's `useCancelAndProceed`.
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@clerk/clerk-react';
import { useAuthContext } from '@/contexts/AuthContext';
import { ContractTerminationsService } from '@/api/services/ContractTerminationsService';
import { OrganisationSettingsService } from '@/api/services/OrganisationSettingsService';
import { workflowKeys } from '@/hooks/useWorkflows';
import { invalidateMoneyViews } from '@/hooks/reconciliation/invalidateMoneyViews';
import type {
  InitiateTerminationRequest,
  ProcessTerminationRequest,
  RevokeTerminationRequest,
} from '@/types/contractTermination';
import { contractVersionKeys } from '@/hooks/useVersionOperations';
import { useModuleGate } from '@/hooks/useAccess';
import { WORKFLOW_SETTINGS_READ_MODULES } from '@/lib/modules';
import { transitionErrorText } from '@/lib/alreadyHandled';
import { msg } from '@lingui/macro';
import { toast } from 'sonner';
import { useLingui } from '@lingui/react';

export const contractTerminationKeys = {
  all: ['contract-terminations'] as const,
  plans: () => [...contractTerminationKeys.all, 'plan'] as const,
  plan: (orgId: string, id: string, effectiveEndDate?: string) =>
    [...contractTerminationKeys.plans(), orgId, id, effectiveEndDate ?? ''] as const,
  settings: () => [...contractTerminationKeys.all, 'settings'] as const,
  revokePlans: () => [...contractTerminationKeys.all, 'revoke-plan'] as const,
  revokePlan: (orgId: string, contractGroupId: string, newEndDate?: string) =>
    [...contractTerminationKeys.revokePlans(), orgId, contractGroupId, newEndDate ?? ''] as const,
};

/** Resolved default notice period (days) when settings are unavailable. */
export const DEFAULT_NOTICE_PERIOD_DAYS = 90;

/**
 * Resolves the per-org `contract_termination.notice_period_days` used to prefill
 * the intake dialog's end date. Falls back to {@link DEFAULT_NOTICE_PERIOD_DAYS}.
 */
export function useContractTerminationSettings() {
  const { isSignedIn, orgId } = useAuth();
  const { isAuthReady } = useAuthContext();
  // `GET /organisations/:id/workflow-settings` admits `workflows` and
  // `contracts-tariffs` (#1691); other roles get the default notice period.
  const canReadSettings = useModuleGate(WORKFLOW_SETTINGS_READ_MODULES);

  const query = useQuery({
    queryKey: [...contractTerminationKeys.settings(), orgId ?? ''],
    queryFn: () => {
      if (!orgId) throw new Error('No organisation ID available');
      return OrganisationSettingsService.getApiV1OrganisationsWorkflowSettings(orgId);
    },
    enabled: isSignedIn && isAuthReady && !!orgId && canReadSettings,
    staleTime: 300000,
  });

  const noticePeriodDays =
    (canReadSettings ? query.data?.contract_termination?.notice_period_days : undefined) ??
    DEFAULT_NOTICE_PERIOD_DAYS;

  return { ...query, noticePeriodDays };
}

/**
 * Initiate a contract termination / cancellation. Creates the workflow item and
 * returns it (the caller navigates to the Review screen using the item id).
 */
export function useInitiateTermination() {
  const queryClient = useQueryClient();
  const { i18n } = useLingui();

  return useMutation({
    mutationFn: (request: InitiateTerminationRequest) =>
      ContractTerminationsService.postApiV1ContractTerminations(request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: workflowKeys.lists() });
      queryClient.invalidateQueries({ queryKey: workflowKeys.pendingCount() });
      queryClient.invalidateQueries({ queryKey: workflowKeys.stats() });
      toast.success(
        i18n._({
          id: 'termination.toast.initiated',
          message: 'Termination workflow created',
        }),
      );
    },
    onError: (error: Error) => {
      toast.error(
        transitionErrorText(
          error,
          i18n,
          msg({
            id: 'termination.toast.initiateError',
            message: 'Failed to create termination workflow',
          }),
        ),
      );
    },
  });
}

/**
 * Read-only dry-run plan for the Review & Confirm screen. Re-fetches when the
 * admin edits the effective end date.
 */
export function useTerminationPlan(
  id: string,
  effectiveEndDate?: string,
  options?: { enabled?: boolean },
) {
  const { isSignedIn, orgId } = useAuth();
  const { isAuthReady } = useAuthContext();

  return useQuery({
    queryKey: contractTerminationKeys.plan(orgId ?? '', id, effectiveEndDate),
    queryFn: () =>
      ContractTerminationsService.getApiV1ContractTerminationsPlan(
        id,
        effectiveEndDate,
      ),
    enabled: (options?.enabled ?? true) && !!id && !!orgId && isSignedIn && isAuthReady,
    staleTime: 15000,
  });
}

/**
 * Execute the termination saga. Returns 200 even on a critical-step failure
 * (`completed === false` with per-step detail); the caller inspects `completed`.
 */
export function useProcessTermination(id: string) {
  const queryClient = useQueryClient();
  const { i18n } = useLingui();

  return useMutation({
    mutationFn: (request: ProcessTerminationRequest) =>
      ContractTerminationsService.postApiV1ContractTerminationsProcess(id, request),
    onSuccess: async (data) => {
      queryClient.invalidateQueries({ queryKey: workflowKeys.lists() });
      queryClient.invalidateQueries({ queryKey: workflowKeys.pendingCount() });
      queryClient.invalidateQueries({ queryKey: workflowKeys.stats() });
      queryClient.invalidateQueries({ queryKey: workflowKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: contractTerminationKeys.plans() });
      // The saga mutates invoices/contracts/allocations — drop those caches.
      // Contract lifecycle writes change what is billed and what is owed, so
      // the invoice, debtor and subsidy surfaces must move with them — in this
      // tab and in the operator's other browser tabs.
      await invalidateMoneyViews(queryClient);
      queryClient.invalidateQueries({ queryKey: ['allocations'] });
      if (data.completed) {
        toast.success(
          i18n._({
            id: 'termination.toast.processed',
            message: 'Termination completed',
          }),
        );
      } else {
        toast.warning(
          i18n._({
            id: 'termination.toast.processIncomplete',
            message: 'Termination incomplete — review the step results',
          }),
        );
      }
    },
    onError: (error: Error) => {
      toast.error(
        transitionErrorText(
          error,
          i18n,
          msg({ id: 'termination.toast.processError', message: 'Failed to process termination' }),
        ),
      );
    },
  });
}

/**
 * Read-only revoke plan for a contract group (#1678). Re-fetches when the admin
 * changes the new end date, because the allocation clamp depends on it.
 */
export function useRevokePlan(
  contractGroupId: string,
  newEndDate?: string,
  options?: { enabled?: boolean },
) {
  const { isSignedIn, orgId } = useAuth();
  const { isAuthReady } = useAuthContext();

  return useQuery({
    queryKey: contractTerminationKeys.revokePlan(orgId ?? '', contractGroupId, newEndDate),
    queryFn: () =>
      ContractTerminationsService.getApiV1ContractGroupsRevokePlan(contractGroupId, newEndDate),
    enabled:
      (options?.enabled ?? true) && !!contractGroupId && !!orgId && isSignedIn && isAuthReady,
    staleTime: 0,
  });
}

/**
 * Revoke the termination of a contract group (#1678). Returns 200 even when a
 * critical step failed (`completed === false`); the caller shows the steps.
 */
export function useRevokeTermination(contractGroupId: string) {
  const queryClient = useQueryClient();
  const { i18n } = useLingui();

  return useMutation({
    mutationFn: (request: RevokeTerminationRequest) =>
      ContractTerminationsService.postApiV1ContractGroupsRevoke(contractGroupId, request),
    onSuccess: async (data) => {
      queryClient.invalidateQueries({ queryKey: workflowKeys.all });
      queryClient.invalidateQueries({ queryKey: contractTerminationKeys.all });
      queryClient.invalidateQueries({ queryKey: contractVersionKeys.all });
      queryClient.invalidateQueries({ queryKey: ['contracts'] });
      queryClient.invalidateQueries({ queryKey: ['allocations'] });
      // The revoke cancels and regenerates invoices: the money views move too.
      await invalidateMoneyViews(queryClient);
      if (data.completed) {
        toast.success(
          i18n._({ id: 'termination.revoke.toast.done', message: 'Termination revoked' }),
        );
      } else {
        toast.warning(
          i18n._({
            id: 'termination.revoke.toast.incomplete',
            message: 'Revoke incomplete — review the step results',
          }),
        );
      }
    },
    onError: (error: unknown) => {
      toast.error(
        transitionErrorText(
          error,
          i18n,
          msg({ id: 'termination.revoke.toast.error', message: 'Failed to revoke the termination' }),
        ),
      );
    },
  });
}
